import { TursoOwnershipError } from './worker-error'
import * as Comlink from 'comlink'
import { createOwnedReplicaTransport } from './injected-replica-transport'
import { deserializeWorkerError, deserializeReplicaState, receiveReplicaCompletion, type TursoReplicaState } from './turso-worker-proxy'
import { preparePageWasm } from './turso-wasm-module'
import type { SharedWorkerClient, SharedRpcRequest, ResolvedTursoWorkerDriverOptions } from './create-turso-worker-driver'
import type { WorkerReplicaEvent, WorkerCallError, WorkerCallEnvelope } from './turso-worker-driver'
import type { FailureEvent } from './failure-commands'

type Response = { type: string; epoch?: string; requestId: number; result?: unknown; error?: WorkerCallError; event?: WorkerReplicaEvent; ids?: number[] }
type Discovery = { type: string; epoch?: string; clientId?: string; message?: Response }
// Classify the outer statement, skipping quoted text, comments and CTE bodies.
// A query API can also carry mutations with RETURNING, which must never replay.
function isRetryableRead(sql: string): boolean {
  const tokens = sql.match(/--[^\r\n]*|\/\*[\s\S]*?\*\/|'(?:''|[^'])*'|"(?:""|[^"])*"|`(?:``|[^`])*`|\[[^\]]*\]|[a-zA-Z_][a-zA-Z_0-9]*|[^\s]/g) ?? []
  let depth = 0
  let command = ''
  let ended = false
  for (const token of tokens) {
    if (token.startsWith('--') || token.startsWith('/*')) continue
    if (ended) { if (token !== ';') return false; continue }
    if (token === '(') { depth++; continue }
    if (token === ')') { if (--depth < 0) return false; continue }
    if (depth !== 0) continue
    if (token === ';') { ended = true; continue }
    const word = token.toUpperCase()
    if (!command) {
      if (word === 'WITH') command = 'WITH'
      else if (['SELECT', 'VALUES', 'EXPLAIN'].includes(word)) command = 'read'
      else return false
    } else if (command === 'WITH' && ['SELECT', 'VALUES', 'INSERT', 'UPDATE', 'DELETE', 'REPLACE'].includes(word)) {
      if (word !== 'SELECT' && word !== 'VALUES') return false
      command = 'read'
    }
  }
  return depth === 0 && command === 'read'
}
export function createSharedWorkerClient(sessionId: string, options: ResolvedTursoWorkerDriverOptions): SharedWorkerClient {
  let candidate: Worker
  let initializedCandidate: Worker | undefined
  let restarts = 0
  let retireCandidate = () => Promise.resolve()
  let transactionEpoch: string | undefined
  const discovery = new BroadcastChannel(`turso-owner:${options.path}`)
  function broadcast(message: unknown): void { const send = discovery.postMessage.bind(discovery); send(message) }
  const shared = () => options.createSharedWorker?.('/turso-shared-worker-host.mjs', { type: 'module', name: 'turso-port-rendezvous-v1' }) ?? new SharedWorker('/turso-shared-worker-host.mjs', { type: 'module', name: 'turso-port-rendezvous-v1' })
  let rendezvous: SharedWorker | undefined
  try { rendezvous = shared() } catch { rendezvous?.port.close(); rendezvous = undefined }
  const failureEvents = new Set<(event: FailureEvent) => void>()
  const replicas = new Set<(state: TursoReplicaState) => void>()
  const subscriptions = new Map<number, { id: number; sql: string; listener(): void }>()
  const failureSubscriptions = new Map<string, Omit<SharedRpcRequest, 'requestId' | 'type'>>()
  const pending = new Map<number, { resolve(value: unknown): void; reject(error: Error): void }>()
  let state: TursoReplicaState | undefined
  let sequence = 0
  let closed = false
  let closing = false
  let ownerWatch: AbortController | undefined
  let reconnecting = false
  let connectionFailed = false
  let ownerFailure: Error | undefined
  let epoch: string | undefined
  let port: MessagePort | undefined
  let opened = false
  let open: Omit<SharedRpcRequest, 'requestId' | 'type'> | undefined
  let resolveReady!: () => void
  let rejectReady!: (error: Error) => void
  let resolveConnected = () => {}
  let ready = new Promise<void>((resolve, reject) => { resolveReady = resolve; rejectReady = reject })
  void ready.catch(() => {})
  let releaseSession!: () => void
  function holdSession(): Promise<void> {
    const held = new Promise<void>(resolve => { releaseSession = resolve })
    return new Promise<void>(resolve => { void navigator.locks.request(`turso-session:${sessionId}`, async () => { resolve(); await held }) })
  }
  const sessionStarted = holdSession()
  function fail(error: Error): void { for (const request of pending.values()) request.reject(error); pending.clear() }
  function startCandidate(): void {
    const worker = new Worker(new URL('./opfs-owner-worker.ts', import.meta.url), { type: 'module', name: `owner:${options.path}` })
    candidate = worker
    const bridge = options.replicaTransport && createOwnedReplicaTransport(options.replicaTransport, options.tel)
    const channel = bridge && new MessageChannel()
    if (bridge && channel) Comlink.expose(bridge.remote, channel.port1)
    let localOwner = false
    let registration: SharedWorker | undefined
    try { registration = shared() } catch { /* The owner also serves the channel fallback. */ }
    async function retire(restart: boolean): Promise<void> {
      worker.terminate(); bridge?.abort(); await bridge?.join(); channel?.port1.close()
      if (closed && candidate === worker) {
        globalThis.removeEventListener('pagehide', suspend); globalThis.removeEventListener('pageshow', resume)
        document.removeEventListener('freeze', suspend); document.removeEventListener('resume', resume)
      }
      if (restart && !closed && candidate === worker && restarts++ < 2) startCandidate()
    }
    retireCandidate = () => retire(false)
    worker.addEventListener('error', event => {
      event.preventDefault()
      const failure = new TursoOwnershipError('Turso worker failed to start: ' + (event.message || 'Database owner worker failed'), 'unknown', { cause: event.error ?? new Error(event.message) })
      if (!epoch) { rejectReady(failure); fail(failure) }
      else if (localOwner) ownerFailure = failure
      // A losing local candidate cannot invalidate the active remote owner.
      if (localOwner) options.onOwnershipChange?.(false)
      void retire(!epoch)
    })
    worker.addEventListener('message', (event: MessageEvent<{ type?: string; owned?: boolean; message?: string }>) => {
      if (candidate !== worker) return
      if (event.data.type === 'owner') { localOwner = event.data.owned === true; options.onOwnershipChange?.(localOwner) }
      if (event.data.type === 'failed') { rejectReady(new TursoOwnershipError(event.data.message ?? 'Database owner failed', 'not-started')); void retire(false) }
      if (event.data.type === 'fatal' || event.data.type === 'retired') { options.onOwnershipChange?.(false); void retire(event.data.type === 'fatal') }
    })
    void preparePageWasm().then(wasm => {
      if (closed || candidate !== worker) { registration?.port.close(); void retire(false); return null }
      worker.postMessage({ path: options.path, env: options.env, wasm, rendezvous: registration?.port, transport: channel?.port2 }, [...registration ? [registration.port] : [], ...channel ? [channel.port2] : []])
      initializedCandidate = worker
      return null
    }).catch((error: unknown) => { rejectReady(error instanceof Error ? error : new Error(String(error))); void retire(false) })
  }
  async function replaceDeadCandidate(): Promise<boolean> {
    if (restarts >= 2 || closed || (await navigator.locks.query()).held?.some(lock => lock.name === `turso-opfs:${options.path}`)) return false
    await retireCandidate(); restarts++; startCandidate()
    return true
  }
  function receive(message: Response): void {
    if (message.type === 'ready') { resolveConnected(); return }
    if (message.type === 'invalidated') { for (const id of message.ids ?? []) subscriptions.get(id)?.listener(); return }
    if (message.type === 'owner-lost') { if (message.error) ownerFailure = deserializeWorkerError(message.error); return }
    if (message.type === 'replica-event' && message.event) {
      if (message.event.completion) receiveReplicaCompletion(options.tel, message.event.completion)
      if (message.event.failure) for (const listener of failureEvents) listener(message.event.failure)
      if (message.event.state) { state = deserializeReplicaState(message.event.state); for (const listener of replicas) listener(state) }
      return
    }
    const request = pending.get(message.requestId)
    if (!request) return
    pending.delete(message.requestId)
    if (message.error) request.reject(deserializeWorkerError(message.error))
    else { for (const id of (message.result as { invalidated?: number[] } | undefined)?.invalidated ?? []) subscriptions.get(id)?.listener(); request.resolve(message.result) }
  }
  function send<T>(request: Omit<SharedRpcRequest, 'requestId' | 'type'>): Promise<T> {
    const requestId = ++sequence
    return new Promise<T>((resolve, reject) => {
      pending.set(requestId, { resolve(value) { resolve(value as T) }, reject })
      const message = { ...request, type: 'request' as const, requestId }
      if (port) port.postMessage(message, [])
      else broadcast({ type: 'rpc', epoch, clientId: sessionId, message })
    })
  }
  async function connect(nextEpoch: string): Promise<void> {
    if (closed || epoch === nextEpoch) return
    const replacing = epoch !== undefined || reconnecting
    epoch = nextEpoch
    ownerWatch?.abort()
    const watch = new AbortController()
    ownerWatch = watch
    // Browser lock release, never scheduling silence, proves owner death.
    void navigator.locks.request(`turso-opfs:${options.path}`, { signal: watch.signal }, () => {
      if (closed || epoch !== nextEpoch) return
      epoch = undefined; reconnecting = true
      resolveReady()
      ready = new Promise<void>((resolve, reject) => { resolveReady = resolve; rejectReady = reject }); void ready.catch(() => {})
      const cause = ownerFailure; ownerFailure = undefined
      fail(new TursoOwnershipError(cause ? `${cause.message}; request outcome unknown` : 'Database owner worker crashed; request outcome unknown', 'unknown', { cause }))
      options.onOwnershipChange?.(false)
      void retireCandidate().then(() => { if (!closed && !closing) startCandidate(); return null })
    }).catch((error: unknown) => { if (!watch.signal.aborted) { const failure = error instanceof Error ? error : new Error(String(error)); rejectReady(failure); fail(failure) } })
    const previousFailure = ownerFailure; ownerFailure = undefined
    fail(new TursoOwnershipError(previousFailure ? `${previousFailure.message}; request outcome unknown` : 'Database ownership changed; request outcome unknown', 'unknown', { cause: previousFailure }))
    port?.close()
    if (replacing && !reconnecting) { resolveReady(); ready = new Promise<void>((resolve, reject) => { resolveReady = resolve; rejectReady = reject }); void ready.catch(() => {}) }
    reconnecting = false
    const connected = new Promise<void>(resolve => { resolveConnected = resolve })
    await sessionStarted
    if (closed || epoch !== nextEpoch) return
    if (rendezvous) {
      const channel = new MessageChannel(); port = channel.port1
      port.addEventListener('message', (event: MessageEvent<Response>) => { if (epoch === nextEpoch) receive(event.data) }); port.start()
      rendezvous.port.postMessage({ type: 'connect', path: options.path, port: channel.port2 }, [channel.port2])
    } else { port = undefined; broadcast({ type: 'connect', epoch, clientId: sessionId }) }
    const fallback = setTimeout(() => {
      if (epoch !== nextEpoch || closed) return
      port?.close(); port = undefined
      broadcast({ type: 'connect', epoch, clientId: sessionId })
    }, 250)
    try { await connected } finally { clearTimeout(fallback) }
    if (closed || epoch !== nextEpoch) return
    if (replacing && opened && open) {
      await send(open)
      for (const registration of failureSubscriptions.values()) {
        const result = await send<WorkerCallEnvelope<void>>(registration)
        if (!result.ok) throw deserializeWorkerError(result.error)
      }
      for (const [id, subscription] of Array.from(subscriptions)) {
        if (closed || epoch !== nextEpoch) return
        if (subscriptions.get(id) !== subscription) continue
        const nextId = await send<number>({ sessionId, op: 'subscribe', args: [subscription.sql] })
        if (closed || epoch !== nextEpoch) return
        if (subscriptions.get(id) !== subscription) {
          await send({ sessionId, op: 'unsubscribe', args: [nextId] })
          continue
        }
        subscriptions.delete(id); subscription.id = nextId; subscriptions.set(nextId, subscription); subscription.listener()
      }
    }
    resolveReady()
  }
  discovery.addEventListener('message', (event: MessageEvent<Discovery>) => {
    const message = event.data
    if (message.type === 'owner' && message.epoch) {
      const announcedEpoch = message.epoch
      void connect(announcedEpoch).catch((error: unknown) => {
        if (epoch !== announcedEpoch || closed || closing) return
        const failure = error instanceof Error ? error : new Error(String(error))
        connectionFailed = true; rejectReady(failure); fail(failure)
      })
    }
    if (message.type === 'rpc-response' && message.epoch === epoch && message.clientId === sessionId && message.message) receive(message.message)
    if (message.type === 'ready' && message.epoch === epoch && message.clientId === sessionId) resolveConnected()
    // Lock grant or successor announcement handles departure.
  })
  // BFCache/pagehide keeps accepted operations and their physical owner alive.
  // Actual page destruction releases locks in the browser.
  function suspend(): void {}
  function resume(): void { if (!closed) { broadcast({ type: 'hello' }); for (const subscription of subscriptions.values()) subscription.listener() } }
  globalThis.addEventListener('pagehide', suspend)
  globalThis.addEventListener('pageshow', resume)
  document.addEventListener('freeze', suspend)
  document.addEventListener('resume', resume)
  try { startCandidate() }
  catch (error) {
    closed = true; releaseSession(); rendezvous?.port.close(); discovery.close()
    globalThis.removeEventListener('pagehide', suspend); globalThis.removeEventListener('pageshow', resume)
    document.removeEventListener('freeze', suspend); document.removeEventListener('resume', resume)
    throw error
  }
  broadcast({ type: 'hello' })
  async function thisRequest<T>(request: Omit<SharedRpcRequest, 'requestId' | 'type'>, retryRestoration = true): Promise<T> {
    if (closed || closing) throw new Error('Turso worker database closed')
    if (connectionFailed) {
      connectionFailed = false; epoch = undefined; reconnecting = true
      ready = new Promise<void>((resolve, reject) => { resolveReady = resolve; rejectReady = reject }); void ready.catch(() => {})
      broadcast({ type: 'hello' })
    }
    if (request.op === 'open') open = request
    for (;;) {
      const currentReady = ready
      try { await currentReady }
      catch (error) {
        // Registration failed before any SQL was posted. Reattach once; a
        // persistent configuration error still reaches the caller.
        if (connectionFailed && retryRestoration && !closed) return thisRequest<T>(request, false)
        throw error
      }
      if (closed) throw new Error('Turso worker database closed')
      if (currentReady !== ready) continue
      const currentEpoch = epoch
      try { await send({ sessionId, op: 'ping' }) }
      catch (error) {
        if (currentEpoch !== epoch) continue
        if (await replaceDeadCandidate()) {
          const replacement = new Promise<void>(resolve => { const listener = (event: MessageEvent<Discovery>) => { if (event.data.type === 'owner' && event.data.epoch !== currentEpoch) { discovery.removeEventListener('message', listener); resolve() } }; discovery.addEventListener('message', listener) })
          await replacement; continue
        }
        throw new TursoOwnershipError('Database owner unavailable before request started', 'not-started', { cause: error })
      }
      if (currentEpoch === epoch && currentReady === ready) break
    }
    if (['txExec', 'txQuery', 'commitTx', 'rollbackTx'].includes(request.op) && transactionEpoch !== undefined && transactionEpoch !== epoch) throw new TursoOwnershipError('Transaction owner changed; request outcome unknown', 'unknown')
    const sentEpoch = epoch
    let result: T
    try { result = await send<T>(request) }
    catch (error) {
      const safeRead = request.op === 'query' && isRetryableRead(String(request.args?.[0]))
      if (error instanceof TursoOwnershipError && !closed && (safeRead || request.op === 'open' || request.op === 'subscribe') && (reconnecting || sentEpoch !== epoch)) return thisRequest<T>(request)
      throw error
    }
    if (request.op === 'beginTx' && (result as { ok?: boolean })?.ok) transactionEpoch = epoch
    if (request.op === 'commitTx' || request.op === 'rollbackTx') transactionEpoch = undefined
    if (request.op === 'open') opened = true
    if ((result as { ok?: boolean } | null)?.ok) {
      if (request.op === 'subscribeFailures') failureSubscriptions.set(request.args?.[1] as string, request)
      if (request.op === 'unsubscribeFailures') failureSubscriptions.delete(request.args?.[0] as string)
    }
    return result
  }
  return {
    failureEvents, request: thisRequest,
    async subscribeReplica(listener) { await ready; if (closed) throw new Error('Turso worker database closed'); replicas.add(listener); if (state) listener(state); return () => { replicas.delete(listener) } },
    async subscribe(sql, listener) {
      let id: number
      for (;;) {
        const before = epoch
        try { id = await thisRequest<number>({ sessionId, op: 'subscribe', args: [sql] }); break }
        catch (error) { if (!(error instanceof TursoOwnershipError) || before === epoch || closed) throw error }
      }
      const subscription = { id, sql, listener }; subscriptions.set(id, subscription)
      return () => { subscriptions.delete(subscription.id); if (!closed) void thisRequest({ sessionId, op: 'unsubscribe', args: [subscription.id] }).catch(() => {}) }
    },
    async close() {
      if (closed || closing) return
      closing = true
      rejectReady(new Error('Turso worker database closed')); fail(new Error('Turso worker database closed'))
      const closingEpoch = epoch
      try { if (opened && epoch) await send({ sessionId, op: 'close' }) }
      catch (error) { if (!(error instanceof TursoOwnershipError) || epoch === closingEpoch) throw error }
      finally {
        closed = true
        globalThis.removeEventListener('pagehide', suspend); globalThis.removeEventListener('pageshow', resume)
        document.removeEventListener('freeze', suspend); document.removeEventListener('resume', resume)
        ownerWatch?.abort(); rejectReady(new Error('Turso worker database closed')); fail(new Error('Turso worker database closed')); port?.close(); rendezvous?.port.close(); discovery.close(); releaseSession()
        if (initializedCandidate === candidate) candidate.postMessage({ type: 'depart' }, [])
        else await retireCandidate()
        replicas.clear(); subscriptions.clear(); failureSubscriptions.clear(); failureEvents.clear()
      }
    },
  }
}
